import { SPELL_REGISTRY } from '../data/spells.config';
import { useSpellCatalogStore } from '../store/spellCatalogueSlice';
import * as contentUnlock from './ContentUnlockService';

// Per-spell linear mastery 1-5 (cf. `MasteryConfig`). Leveled up by using a
// spell; raises its damage via `applyMasteryBoost`.
/** Live mastery level from the local catalog store (local player / solo). */
export function getMasteryLevelLive(spellId: string): number {
  const def = SPELL_REGISTRY[spellId];
  if (!def?.mastery) {
    return 1;
  }
  const xp = useSpellCatalogStore.getState().masteryXp[spellId] ?? 0;
  let level = 1;
  for (const required of def.mastery.xpPerLevel) {
    if (xp >= required) {
      level += 1;
    }
  }
  return Math.min(5, level);
}

/** Damage boost using the CASTER's mastery level (remote map or live store). */
export function applyMasteryBoost(
  caster: { getMasteryLevel(spellId: string): number },
  spellId: string,
  baseDamage: number,
): number {
  const def = SPELL_REGISTRY[spellId];
  if (!def?.mastery) {
    return baseDamage;
  }
  const level = caster.getMasteryLevel(spellId);
  return baseDamage * (1 + def.mastery.damageBoostPerLevel * (level - 1));
}

export function gainMasteryXp(spellId: string, amount: number): void {
  const def = SPELL_REGISTRY[spellId];
  if (!def?.mastery) {
    return;
  }
  useSpellCatalogStore.getState().gainMasteryXp(spellId, amount);
}

/** Mastery XP granted when a grimoire targets an already-known spell. */
export const GRIMOIRE_XP_FRAGMENT = 100;

/**
 * Learn a spell from a grimoire: unlock it, or — if already known — convert the
 * grimoire into mastery XP. Player-independent: callable from the `unlockSpell`
 * effect handler (in a run) and from hub item use (no dungeon player). Returns
 * `true` if the spell was newly unlocked (vs converted to mastery XP).
 */
export function learnSpellFromGrimoire(spellId: string): boolean {
  const store = useSpellCatalogStore.getState();
  if (store.isUnlocked(spellId)) {
    gainMasteryXp(spellId, GRIMOIRE_XP_FRAGMENT);
    return false;
  }
  contentUnlock.unlockSpell(spellId, { showcase: false });
  return true;
}
